import React, { useEffect, useState, useRef } from 'react';
import { getBookings, deleteBooking, updateBookingStatus } from '../../services/api';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trash2, Download, Eye, X, Printer, FileSpreadsheet, MessageCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { currencySymbol } from '@/lib/utils';

export default function BookingsManagement() {
  const [allBookings, setAllBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [bookingFilter, setBookingFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const printRef = useRef(null);

  const WHATSAPP_NUMBER = '905414112373';

  const paymentLabels = {
    credit_card: 'Kredi Kartı',
    bank_transfer: 'Havale/EFT',
    cash_on_tour: 'Tur Esnasında Ödeme'
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  useEffect(() => {
    if (bookingFilter === 'all') {
      setFilteredBookings(allBookings);
    } else {
      setFilteredBookings(allBookings.filter(b => b.tour_type === bookingFilter));
    }
  }, [bookingFilter, allBookings]);

  const fetchBookings = async () => {
    try {
      const response = await getBookings();
      setAllBookings(response.data);
      setFilteredBookings(response.data);
    } catch (error) {
      toast.error('Rezervasyonlar yüklenirken hata');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu rezervasyonu silmek istediğinizden emin misiniz?')) return;
    try {
      await deleteBooking(id);
      setAllBookings(allBookings.filter((b) => b.id !== id));
      toast.success('Rezervasyon silindi');
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  const handleStatusChange = async (bookingId, newStatus) => {
    try {
      await updateBookingStatus(bookingId, newStatus);
      setAllBookings(allBookings.map(b => 
        b.id === bookingId ? { ...b, status: newStatus } : b
      ));
      toast.success('Durum güncellendi');
    } catch (error) {
      toast.error('Durum güncellenemedi');
    }
  };

  const viewBookingDetails = (booking) => {
    setSelectedBooking(booking);
    setIsDetailOpen(true);
  };

  const exportToCSV = () => {
    const headers = ['Ad', 'Soyad', 'TC Kimlik', 'Doğum Tarihi', 'Tur', 'Tip', 'Tarih', 'Kişi', 'Toplam', 'Para Birimi', 'Ödeme Yöntemi', 'Email', 'Telefon', 'Durum'];
    const rows = filteredBookings.map(b => [
      b.first_name,
      b.last_name,
      b.tc_number || '-',
      b.birth_date || '-',
      b.tour_title,
      b.tour_type === 'istanbul' ? 'İstanbul' : b.tour_type === 'yurtici' ? 'Yurtiçi' : 'Yurtdışı',
      b.selected_date,
      b.number_of_guests,
      b.total_price,
      ({ EUR: 'Euro', USD: 'Dolar' }[b.currency] || 'TL'),
      paymentLabels[b.payment_method] || 'Kredi Kartı',
      b.email,
      b.phone,
      b.status === 'confirmed' ? 'Onaylı' : b.status === 'pending' ? 'Beklemede' : 'İptal'
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `rezervasyonlar_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Excel dosyası indirildi');
  };

  // Export single booking to Excel
  const exportSingleBookingToExcel = (booking) => {
    const currency = currencySymbol(booking.currency);
    const headers = ['Alan', 'Değer'];
    const rows = [
      ['Rezervasyon No', booking.id],
      ['Ad', booking.first_name],
      ['Soyad', booking.last_name],
      ['TC Kimlik No', booking.tc_number || '-'],
      ['Doğum Tarihi', booking.birth_date || '-'],
      ['E-posta', booking.email],
      ['Telefon', booking.phone],
      ['Tur Adı', booking.tour_title],
      ['Tur Tipi', booking.tour_type === 'istanbul' ? 'İstanbul' : booking.tour_type === 'yurtici' ? 'Yurtiçi' : 'Yurtdışı'],
      ['Tarih', booking.selected_date],
      ['Kişi Sayısı', booking.number_of_guests],
      ['Ödeme Yöntemi', paymentLabels[booking.payment_method] || 'Kredi Kartı'],
      ['Toplam Tutar', `${currency}${booking.total_price}`],
      ['Durum', booking.status === 'confirmed' ? 'Onaylı' : booking.status === 'pending' ? 'Beklemede' : 'İptal'],
      ['Özel İstekler', booking.special_requests || '-'],
      ['Rezervasyon Tarihi', booking.booking_date ? new Date(booking.booking_date).toLocaleString('tr-TR') : '-']
    ];
    
    const csvContent = [headers, ...rows].map(row => row.join('\t')).join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `rezervasyon_${booking.id}_${booking.first_name}_${booking.last_name}.csv`;
    link.click();
    toast.success('Rezervasyon Excel dosyası indirildi');
  };

  // Print single booking
  const printBooking = (booking) => {
    const currency = currencySymbol(booking.currency);
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Rezervasyon - ${booking.id}</title>
        <style>
          body { font-family: 'Segoe UI', Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          .header { text-align: center; border-bottom: 3px solid #f97316; padding-bottom: 20px; margin-bottom: 30px; }
          .header h1 { color: #f97316; margin: 0; font-size: 28px; }
          .header p { color: #666; margin: 10px 0 0; }
          .voucher-box { background: #fef3c7; border-left: 4px solid #f97316; padding: 15px 20px; margin-bottom: 25px; }
          .voucher-box h2 { margin: 0 0 5px; color: #92400e; }
          .section { margin-bottom: 25px; }
          .section h3 { border-bottom: 2px solid #f97316; padding-bottom: 8px; color: #1f2937; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }
          .info-item { padding: 10px 0; border-bottom: 1px solid #eee; }
          .info-item label { color: #666; display: block; font-size: 12px; margin-bottom: 4px; }
          .info-item span { font-weight: 600; color: #1f2937; }
          .total-box { background: #f3f4f6; padding: 20px; border-radius: 8px; text-align: right; }
          .total-box .amount { font-size: 28px; color: #f97316; font-weight: bold; }
          .footer { text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #eee; color: #666; font-size: 12px; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>🏛️ İstanbul Turda</h1>
          <p>VIATERRA TRAVEL AGENCY - TÜRSAB: 18741</p>
        </div>
        
        <div class="voucher-box">
          <h2>🎫 REZERVASYON DETAYI</h2>
          <p>Rezervasyon No: <strong>${booking.id}</strong></p>
        </div>
        
        <div class="section">
          <h3>👤 Katılımcı Bilgileri</h3>
          <div class="info-grid">
            <div class="info-item"><label>Ad Soyad</label><span>${booking.first_name} ${booking.last_name}</span></div>
            <div class="info-item"><label>TC Kimlik No</label><span>${booking.tc_number || '-'}</span></div>
            <div class="info-item"><label>Doğum Tarihi</label><span>${booking.birth_date || '-'}</span></div>
            <div class="info-item"><label>E-posta</label><span>${booking.email}</span></div>
            <div class="info-item"><label>Telefon</label><span>${booking.phone}</span></div>
          </div>
        </div>
        
        <div class="section">
          <h3>📍 Tur Bilgileri</h3>
          <div class="info-grid">
            <div class="info-item"><label>Tur Adı</label><span>${booking.tour_title}</span></div>
            <div class="info-item"><label>Tur Tipi</label><span>${booking.tour_type === 'istanbul' ? 'İstanbul' : booking.tour_type === 'yurtici' ? 'Yurtiçi' : 'Yurtdışı'}</span></div>
            <div class="info-item"><label>Tarih</label><span>${booking.selected_date}</span></div>
            <div class="info-item"><label>Kişi Sayısı</label><span>${booking.number_of_guests} Kişi</span></div>
          </div>
        </div>
        
        <div class="section">
          <h3>💳 Ödeme Bilgileri</h3>
          <div class="info-grid">
            <div class="info-item"><label>Ödeme Yöntemi</label><span>${paymentLabels[booking.payment_method] || 'Kredi Kartı'}</span></div>
            <div class="info-item"><label>Durum</label><span>${booking.status === 'confirmed' ? '✅ Onaylı' : booking.status === 'pending' ? '⏳ Beklemede' : '❌ İptal'}</span></div>
          </div>
          <div class="total-box">
            <p style="margin:0 0 5px;color:#666;">Toplam Tutar</p>
            <div class="amount">${currency}${booking.total_price}</div>
          </div>
        </div>
        
        ${booking.special_requests ? `
        <div class="section">
          <h3>📝 Özel İstekler</h3>
          <p style="background:#f9fafb;padding:15px;border-radius:8px;">${booking.special_requests}</p>
        </div>
        ` : ''}
        
        <div class="footer">
          <p>📞 +90 541 411 23 73 | ✉️ info@istanbulturda.com</p>
          <p>Truva Villaları No:8, Esenkent Mh, 34510 Esenyurt/İstanbul</p>
          <p style="margin-top:15px;font-size:10px;">Bu belge ${new Date().toLocaleString('tr-TR')} tarihinde oluşturulmuştur.</p>
        </div>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  // WhatsApp message for booking
  const sendWhatsAppMessage = (booking) => {
    const currency = currencySymbol(booking.currency);
    const message = `🎫 *REZERVASYON BİLGİLERİ*

📋 *Rezervasyon No:* ${booking.id}
👤 *Ad Soyad:* ${booking.first_name} ${booking.last_name}
🆔 *TC:* ${booking.tc_number || '-'}
📞 *Telefon:* ${booking.phone}
✉️ *Email:* ${booking.email}

📍 *Tur:* ${booking.tour_title}
📅 *Tarih:* ${booking.selected_date}
👥 *Kişi:* ${booking.number_of_guests}
💳 *Ödeme:* ${paymentLabels[booking.payment_method] || 'Kredi Kartı'}
💰 *Toplam:* ${currency}${booking.total_price}

✅ *Durum:* ${booking.status === 'confirmed' ? 'Onaylı' : booking.status === 'pending' ? 'Beklemede' : 'İptal'}`;
    
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const getBookingCounts = () => ({
    all: allBookings.length,
    istanbul: allBookings.filter(b => b.tour_type === 'istanbul').length,
    yurtici: allBookings.filter(b => b.tour_type === 'yurtici').length,
    yurtdisi: allBookings.filter(b => b.tour_type === 'yurtdisi').length,
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'confirmed':
        return <span className="px-3 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700">Onaylı</span>;
      case 'pending':
        return <span className="px-3 py-1 rounded-full text-xs font-bold bg-yellow-100 text-yellow-700">Beklemede</span>;
      case 'cancelled':
        return <span className="px-3 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700">İptal</span>;
      default:
        return <span className="px-3 py-1 rounded-full text-xs font-bold bg-gray-100 text-gray-700">{status}</span>;
    }
  };

  const counts = getBookingCounts();

  if (loading) return <div className="text-center py-12">Yükleniyor...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Rezervasyon Yönetimi</h2>
        <Button onClick={exportToCSV} className="bg-green-600 hover:bg-green-700 text-white" data-testid="export-csv-btn">
          <Download size={20} className="mr-2" /> Excel'e Aktar
        </Button>
      </div>

      <div className="mb-6 flex flex-wrap gap-3">
        <button onClick={() => setBookingFilter('all')} className={`px-6 py-2 rounded-full font-ui font-bold transition-colors ${ bookingFilter === 'all' ? 'bg-orange-500 text-white' : 'bg-white border-2 text-gray-700 hover:border-orange-500' }`} data-testid="filter-all">Tümü ({counts.all})</button>
        <button onClick={() => setBookingFilter('istanbul')} className={`px-6 py-2 rounded-full font-ui font-bold transition-colors ${ bookingFilter === 'istanbul' ? 'bg-orange-500 text-white' : 'bg-white border-2 text-gray-700 hover:border-orange-500' }`} data-testid="filter-istanbul">İstanbul ({counts.istanbul})</button>
        <button onClick={() => setBookingFilter('yurtici')} className={`px-6 py-2 rounded-full font-ui font-bold transition-colors ${ bookingFilter === 'yurtici' ? 'bg-orange-500 text-white' : 'bg-white border-2 text-gray-700 hover:border-orange-500' }`} data-testid="filter-yurtici">Yurtiçi ({counts.yurtici})</button>
        <button onClick={() => setBookingFilter('yurtdisi')} className={`px-6 py-2 rounded-full font-ui font-bold transition-colors ${ bookingFilter === 'yurtdisi' ? 'bg-orange-500 text-white' : 'bg-white border-2 text-gray-700 hover:border-orange-500' }`} data-testid="filter-yurtdisi">Yurtdışı ({counts.yurtdisi})</button>
      </div>

      <div className="bg-white rounded-2xl border overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-4 py-4 text-left text-sm font-bold">Ad Soyad</th>
              <th className="px-4 py-4 text-left text-sm font-bold">TC Kimlik</th>
              <th className="px-4 py-4 text-left text-sm font-bold">Doğum Tarihi</th>
              <th className="px-4 py-4 text-left text-sm font-bold">Tur</th>
              <th className="px-4 py-4 text-left text-sm font-bold">Tip</th>
              <th className="px-4 py-4 text-left text-sm font-bold">Tarih</th>
              <th className="px-4 py-4 text-left text-sm font-bold">Kişi</th>
              <th className="px-4 py-4 text-left text-sm font-bold">Toplam</th>
              <th className="px-4 py-4 text-left text-sm font-bold">Durum</th>
              <th className="px-4 py-4 text-left text-sm font-bold">İşlem</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {filteredBookings.length === 0 ? (
              <tr><td colSpan="10" className="px-6 py-12 text-center text-gray-500">Rezervasyon bulunamadı</td></tr>
            ) : (
              filteredBookings.map((booking) => (
                <tr key={booking.id} className="hover:bg-gray-50" data-testid={`booking-row-${booking.id}`}>
                  <td className="px-4 py-4 text-sm font-medium">{booking.first_name} {booking.last_name}</td>
                  <td className="px-4 py-4 text-sm font-mono">{booking.tc_number || '-'}</td>
                  <td className="px-4 py-4 text-sm">{booking.birth_date || '-'}</td>
                  <td className="px-4 py-4 text-sm max-w-[200px] truncate" title={booking.tour_title}>{booking.tour_title}</td>
                  <td className="px-4 py-4 text-sm">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${ booking.tour_type === 'istanbul' ? 'bg-blue-100 text-blue-700' : booking.tour_type === 'yurtici' ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700' }`}>
                      {booking.tour_type === 'istanbul' ? 'İstanbul' : booking.tour_type === 'yurtici' ? 'Yurtiçi' : 'Yurtdışı'}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm">{booking.selected_date}</td>
                  <td className="px-4 py-4 text-sm text-center">{booking.number_of_guests}</td>
                  <td className="px-4 py-4 text-sm font-bold text-orange-500">
                    {currencySymbol(booking.currency)}{booking.total_price}
                  </td>
                  <td className="px-4 py-4 text-sm">
                    <Select value={booking.status} onValueChange={(value) => handleStatusChange(booking.id, value)}>
                      <SelectTrigger className="w-32 h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="confirmed">Onaylı</SelectItem>
                        <SelectItem value="pending">Beklemede</SelectItem>
                        <SelectItem value="cancelled">İptal</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => viewBookingDetails(booking)} title="Detay" data-testid={`view-booking-${booking.id}`}>
                        <Eye size={14} />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => printBooking(booking)} title="Yazdır" className="text-blue-600 hover:text-blue-700">
                        <Printer size={14} />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => exportSingleBookingToExcel(booking)} title="Excel" className="text-green-600 hover:text-green-700">
                        <FileSpreadsheet size={14} />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => sendWhatsAppMessage(booking)} title="WhatsApp" className="text-green-500 hover:text-green-600">
                        <MessageCircle size={14} />
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => handleDelete(booking.id)} title="Sil" data-testid={`delete-booking-${booking.id}`}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Booking Detail Modal */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Rezervasyon Detayları</span>
            </DialogTitle>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Ad</p>
                  <p className="font-medium">{selectedBooking.first_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Soyad</p>
                  <p className="font-medium">{selectedBooking.last_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">TC Kimlik No</p>
                  <p className="font-mono font-medium">{selectedBooking.tc_number || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Doğum Tarihi</p>
                  <p className="font-medium">{selectedBooking.birth_date || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">E-posta</p>
                  <p className="font-medium">{selectedBooking.email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Telefon</p>
                  <p className="font-medium">{selectedBooking.phone}</p>
                </div>
              </div>
              <hr />
              <div>
                <p className="text-sm text-gray-500">Tur</p>
                <p className="font-medium">{selectedBooking.tour_title}</p>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Tarih</p>
                  <p className="font-medium">{selectedBooking.selected_date}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Kişi Sayısı</p>
                  <p className="font-medium">{selectedBooking.number_of_guests}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Toplam</p>
                  <p className="font-bold text-orange-500">{currencySymbol(selectedBooking.currency)}{selectedBooking.total_price}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Ödeme Yöntemi</p>
                  <p className="font-medium">{paymentLabels[selectedBooking.payment_method] || 'Kredi Kartı'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Durum</p>
                  {getStatusBadge(selectedBooking.status)}
                </div>
              </div>
              {selectedBooking.special_requests && (
                <div>
                  <p className="text-sm text-gray-500">Özel İstekler</p>
                  <p className="font-medium bg-gray-50 p-3 rounded-lg">{selectedBooking.special_requests}</p>
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t">
                <Button onClick={() => printBooking(selectedBooking)} className="flex-1 bg-blue-600 hover:bg-blue-700">
                  <Printer size={16} className="mr-2" /> Yazdır
                </Button>
                <Button onClick={() => exportSingleBookingToExcel(selectedBooking)} className="flex-1 bg-green-600 hover:bg-green-700">
                  <FileSpreadsheet size={16} className="mr-2" /> Excel
                </Button>
                <Button onClick={() => sendWhatsAppMessage(selectedBooking)} className="flex-1 bg-green-500 hover:bg-green-600">
                  <MessageCircle size={16} className="mr-2" /> WhatsApp
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
